/**
 * Dashboard - Main dashboard overview for Admin Portal
 * 
 * Displays key metrics, recent activity, and quick actions.
 * Uses skeleton loaders and caching for optimized performance.
 * 
 * Migrated from src/app/components/admin/AdminDashboard.jsx
 */

import { useState, useEffect, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import AdminCache from '../components/shared/AdminCache';
import HelpPanel from '../components/shared/HelpPanel';
import HelpTooltip from '../components/shared/HelpTooltip';
import PieChart from '../components/charts/PieChart';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { generateMockUsers, isMockEnabled, getMockUserCount } from '../utils/mockUsers';

// Destructure skeleton components
const { Skeleton, StatCardSkeleton, ActivityListSkeleton, DashboardSkeleton } = SkeletonLoaderComponents;

// Admin Audit Logger Function URL
const AUDIT_LOGGER_URL = API_ENDPOINTS.ADMIN.AUDIT_LOGGER;

// Help content for dashboard
const HELP_CONTENT = {
  dashboard: {
    totalUsers: 'Total number of registered users in the system',
    activeSubscriptions: 'Number of users with active paid subscriptions',
    monthlyRevenue: 'Total revenue generated this month from subscriptions and payments'
  }
};

// Memoized stat card component for better performance
const StatCard = memo(({ icon, iconColor, value, label, change, changeType, helpText }) => (
  <div className="admin-stat-card">
    <div className={`admin-stat-icon ${iconColor}`}>{icon}</div>
    <div className="admin-stat-value">{value}</div>
    <div className="admin-stat-label">
      {label}
      {helpText && <HelpTooltip content={helpText} position="top" size="small" />}
    </div>
    {change && (
      <div className={`admin-stat-change ${changeType}`}>{change}</div>
    )}
  </div>
));

StatCard.displayName = 'StatCard';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [userActivity, setUserActivity] = useState(null); // NEW: User activity breakdown
  const [recentActivity, setRecentActivity] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);

  // Generate mock users once (memoized)
  const mockUsers = isMockEnabled() ? generateMockUsers() : [];

  // Load dashboard data with caching
  const loadDashboardData = useCallback(async () => {
    // Check cache first for stats
    const cachedStats = AdminCache.get(AdminCache.KEYS.DASHBOARD_STATS);
    if (cachedStats) {
      setStats(cachedStats);
      setLoading(false);
      return;
    }

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch real data from multiple endpoints in parallel
      const [analyticsRes, revenueRes, webhooksRes] = await Promise.allSettled([
        fetch(`${API_ENDPOINTS.ADMIN.ANALYTICS}/users`, { headers }),
        fetch(`${API_ENDPOINTS.ADMIN.ANALYTICS}/revenue`, { headers }),
        fetch(`${API_ENDPOINTS.ADMIN.WEBHOOK_MANAGER}/admin/webhooks?status=pending`, { headers })
      ]);

      // Extract user count and activity summary from analytics endpoint
      let totalUsers = 0;
      let calculatedActivity = { active: 0, inactive: 0, zombie: 0 };
      
      if (analyticsRes.status === 'fulfilled' && analyticsRes.value.ok) {
        const data = await analyticsRes.value.json();
        const analytics = data.analytics;
        
        // Get total users from analytics summary
        totalUsers = analytics.summary?.totalUsers || 0;
        
        // Calculate activity breakdown from analytics data
        const wau = analytics.activity?.wau || 0;  // Weekly active users (last 7 days)
        const mau = analytics.activity?.mau || 0;  // Monthly active users (last 30 days)
        
        calculatedActivity = {
          active: wau,                    // Logged in within 7 days
          inactive: Math.max(0, mau - wau),  // Logged in 8-30 days ago
          zombie: Math.max(0, totalUsers - mau)  // >30 days or never logged in
        };
      }
      
      // Add mock users to stats if enabled
      if (mockUsers.length > 0) {
        const mockActivityCount = { active: 0, inactive: 0, zombie: 0 };
        const now = Date.now();
        const DAY = 86400000;
        
        mockUsers.forEach(user => {
          if (!user.lastLoginAt) {
            mockActivityCount.zombie++;
          } else {
            const daysSince = (now - new Date(user.lastLoginAt).getTime()) / DAY;
            if (daysSince <= 7) mockActivityCount.active++;
            else if (daysSince <= 30) mockActivityCount.inactive++;
            else mockActivityCount.zombie++;
          }
        });
        
        totalUsers += mockUsers.length;
        calculatedActivity.active += mockActivityCount.active;
        calculatedActivity.inactive += mockActivityCount.inactive;
        calculatedActivity.zombie += mockActivityCount.zombie;
        
        console.log('[Dashboard] Mock users added to stats:', {
          mockTotal: mockUsers.length,
          mockActivity: mockActivityCount,
          newTotal: totalUsers,
          newActivity: calculatedActivity
        });
      }
      
      // Store activity summary for activity stat cards
      setUserActivity(calculatedActivity);

      // Extract analytics (subscriptions + revenue)
      let activeSubscriptions = 0;
      let monthlyRevenue = 0;
      if (revenueRes.status === 'fulfilled' && revenueRes.value.ok) {
        const data = await revenueRes.value.json();
        // Revenue endpoint returns: { mrr, arr, arpu, ltv, subscriptions }
        activeSubscriptions = data.subscriptions?.active || 0;
        monthlyRevenue = data.mrr || 0;
      }

      // Add mock subscriptions and revenue if enabled
      if (mockUsers.length > 0) {
        // Count subscriptions by tier
        const tierCounts = { premium: 0, family: 0 };
        mockUsers.forEach(user => {
          if (user.tier === 'premium') tierCounts.premium++;
          else if (user.tier === 'family') tierCounts.family++;
        });
        
        // Actual pricing from region-config.js (HKD)
        // Premium: HK$398/month, Family: HK$888/month
        const PREMIUM_PRICE_HKD = 398;
        const FAMILY_PRICE_HKD = 888;
        
        const mockSubscriptions = tierCounts.premium + tierCounts.family;
        const mockRevenueHKD = (tierCounts.premium * PREMIUM_PRICE_HKD) + (tierCounts.family * FAMILY_PRICE_HKD);
        
        activeSubscriptions += mockSubscriptions;
        monthlyRevenue += mockRevenueHKD;
        
        console.log('[Dashboard] Mock subscriptions added:', {
          premium: tierCounts.premium,
          family: tierCounts.family,
          mockSubscriptions,
          mockRevenueHKD: `HK$${mockRevenueHKD.toLocaleString()}`
        });
      }

      // Extract pending webhooks
      let pendingWebhooks = 0;
      if (webhooksRes.status === 'fulfilled' && webhooksRes.value.ok) {
        const data = await webhooksRes.value.json();
        pendingWebhooks = data.total || data.webhooks?.length || 0;
      }

      // Add mock pending webhooks (random 3-8 for demo)
      if (mockUsers.length > 0) {
        const mockPendingWebhooks = Math.floor(Math.random() * 6) + 3; // 3-8 webhooks
        pendingWebhooks += mockPendingWebhooks;
        console.log('[Dashboard] Mock pending webhooks:', mockPendingWebhooks);
      }

      const statsData = {
        totalUsers,
        activeSubscriptions,
        monthlyRevenue,
        pendingWebhooks
      };
      setStats(statsData);
      AdminCache.set(AdminCache.KEYS.DASHBOARD_STATS, statsData, AdminCache.TTL.short);
    } catch (err) {
      console.warn('[Dashboard] Failed to load stats:', err);
      // Show zeros rather than fake data
      setStats({
        totalUsers: 0,
        activeSubscriptions: 0,
        monthlyRevenue: 0,
        pendingWebhooks: 0
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecentActivity = useCallback(async () => {
    // Check cache first
    const cachedActivity = AdminCache.get(AdminCache.KEYS.AUDIT_LOGS + '_recent');
    if (cachedActivity) {
      setRecentActivity(cachedActivity);
      setActivityLoading(false);
      return;
    }

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${AUDIT_LOGGER_URL}/logs?limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Audit log fetch failed: ${response.status}`);
      }

      const data = await response.json();
      const logs = data.data?.logs || data.logs || [];
      setRecentActivity(logs);
      if (logs.length > 0) {
        AdminCache.set(AdminCache.KEYS.AUDIT_LOGS + '_recent', logs, AdminCache.TTL.short);
      }
    } catch (err) {
      console.warn('[Dashboard] Failed to load recent activity:', err);
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const checkSystemHealth = useCallback(async () => {
    console.log('[Dashboard] Starting system health check...');
    
    // Check cache first
    const cachedHealth = AdminCache.get(AdminCache.KEYS.SYSTEM_HEALTH + '_quick');
    if (cachedHealth) {
      console.log('[Dashboard] Using cached health data:', cachedHealth);
      setSystemHealth(cachedHealth);
      setHealthLoading(false);
      return;
    }

    try {
      console.log('[Dashboard] Fetching health data from endpoints...');
      console.log('[Dashboard] Endpoints:', {
        audit: AUDIT_LOGGER_URL,
        analytics: `${API_ENDPOINTS.ADMIN.ANALYTICS}/health`,
        payment: API_ENDPOINTS.ADMIN.PAYMENT_ANALYTICS
      });
      
      // Add timeout to fetch requests
      const fetchWithTimeout = (url, timeout = 5000) => {
        return Promise.race([
          fetch(url).then(res => {
            console.log(`[Dashboard] Response from ${url}:`, res.status);
            return res.json();
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), timeout)
          )
        ]);
      };

      // Check all three services in parallel (no auth required for health checks)
      const [apiCheck, dbCheck, paymentCheck] = await Promise.allSettled([
        // API Services - Check audit logger (root endpoint is health check)
        fetchWithTimeout(AUDIT_LOGGER_URL),
        
        // Database - Check analytics handler (uses DynamoDB)
        fetchWithTimeout(`${API_ENDPOINTS.ADMIN.ANALYTICS}/health`),
        
        // Payment Processing - Check payment analytics (root endpoint is health check)
        fetchWithTimeout(API_ENDPOINTS.ADMIN.PAYMENT_ANALYTICS)
      ]);

      console.log('[Dashboard] Health check results:', { apiCheck, dbCheck, paymentCheck });

      const healthData = {
        api: apiCheck.status === 'fulfilled' && apiCheck.value?.status === 'healthy' ? 'healthy' : 'error',
        database: dbCheck.status === 'fulfilled' && dbCheck.value?.status === 'healthy' ? 'healthy' : 'error',
        payments: paymentCheck.status === 'fulfilled' && paymentCheck.value?.status === 'healthy' ? 'healthy' : 'error'
      };
      
      console.log('[Dashboard] Setting health data:', healthData);
      setSystemHealth(healthData);
      AdminCache.set(AdminCache.KEYS.SYSTEM_HEALTH + '_quick', healthData, AdminCache.TTL.short);
    } catch (err) {
      console.error('[Dashboard] System health check failed:', err);
      setSystemHealth({
        api: 'error',
        database: 'error',
        payments: 'error'
      });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load all data in parallel for faster initial load
    Promise.all([
      loadDashboardData(),
      loadRecentActivity(),
      checkSystemHealth()
    ]);
  }, [loadDashboardData, loadRecentActivity, checkSystemHealth]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return '#22c55e';
      case 'degraded': return '#f59e0b';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Show skeleton while initial data is loading
  if (loading && !stats) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="admin-dashboard">
      {/* Mock Mode Banner */}
      {mockUsers.length > 0 && (
        <div className="admin-alert admin-alert--warning" style={{ marginBottom: '1rem' }}>
          <span>🎭</span> <strong>Demo Mode Active</strong> — Showing {mockUsers.length.toLocaleString()} mock users alongside real data for panel demo. 
          <span style={{ marginLeft: 'auto', fontSize: '0.875rem' }}>
            Total: {stats?.totalUsers?.toLocaleString() || 0} users
          </span>
        </div>
      )}
      
      {/* Inline Help Panel */}
      <HelpPanel section="dashboard" />
      
      {/* Stats Grid */}
      <div className="admin-grid admin-grid-4">
        {stats ? (
          <>
            <StatCard
              icon="👥"
              iconColor="blue"
              value={stats.totalUsers.toLocaleString()}
              label="Total Users"
              helpText={HELP_CONTENT.dashboard.totalUsers}
            />
            <StatCard
              icon="💳"
              iconColor="green"
              value={stats.activeSubscriptions.toLocaleString()}
              label="Active Subscriptions"
              helpText={HELP_CONTENT.dashboard.activeSubscriptions}
            />
            <StatCard
              icon="💰"
              iconColor="purple"
              value={formatCurrency(stats.monthlyRevenue)}
              label="Monthly Revenue"
              helpText={HELP_CONTENT.dashboard.monthlyRevenue}
            />
            <StatCard
              icon="🔗"
              iconColor="orange"
              value={stats.pendingWebhooks}
              label="Pending Webhooks"
              change={stats.pendingWebhooks > 0 ? "Needs attention" : null}
              changeType={stats.pendingWebhooks > 0 ? "negative" : null}
            />
          </>
        ) : (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        )}
      </div>

      {/* User Activity Breakdown */}
      {userActivity && (
        <div className="admin-section-gap">
          <h3 className="admin-section-title">User Activity (Last 30 Days)</h3>
          <div className="admin-grid admin-grid-3">
            <StatCard
              icon="🟢"
              iconColor="green"
              value={userActivity.active || 0}
              label="Active Users"
              helpText="Users who logged in within the last 7 days"
              change={`${Math.round((userActivity.active / stats.totalUsers) * 100)}% of total`}
              changeType="positive"
            />
            <StatCard
              icon="🟡"
              iconColor="yellow"
              value={userActivity.inactive || 0}
              label="Inactive Users"
              helpText="Users who logged in 8-30 days ago"
              change={`${Math.round((userActivity.inactive / stats.totalUsers) * 100)}% of total`}
              changeType="neutral"
            />
            <StatCard
              icon="🔴"
              iconColor="red"
              value={userActivity.zombie || 0}
              label="Zombie Users"
              helpText="Users who haven't logged in for over 30 days or never logged in"
              change={`${Math.round((userActivity.zombie / stats.totalUsers) * 100)}% of total`}
              changeType="negative"
            />
          </div>

          {/* Activity Breakdown Chart */}
          <div className="admin-card" style={{ marginTop: '2rem' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">Activity Breakdown</h3>
            </div>
            <PieChart
              data={{
                labels: ['Active', 'Inactive', 'Zombie'],
                datasets: [{
                  data: [
                    userActivity.active || 0,
                    userActivity.inactive || 0,
                    userActivity.zombie || 0
                  ],
                  backgroundColor: ['#10B981', '#F59E0B', '#EF4444']
                }]
              }}
              title="User Activity Distribution"
              variant="doughnut"
              height={350}
              loading={activityLoading}
              emptyMessage="No activity data available"
              showPercentage={true}
            />
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="admin-grid admin-grid-2 admin-section-gap">
        {/* Recent Activity */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Recent Activity</h3>
            <Link to="/audit-logs" className="admin-btn admin-btn-secondary">
              View All
            </Link>
          </div>
          
          {activityLoading ? (
            <ActivityListSkeleton count={5} />
          ) : recentActivity && recentActivity.length === 0 ? (
            <div className="admin-empty">
              <div className="admin-empty-icon">📋</div>
              <div className="admin-empty-title">No recent activity</div>
              <div className="admin-empty-text">Activity will appear here as actions are performed.</div>
            </div>
          ) : (
            <div className="activity-list">
              {(recentActivity || []).map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon">
                    {getActivityIcon(activity.action)}
                  </div>
                  <div className="activity-content">
                    <div className="activity-action">{formatAction(activity.action)}</div>
                    <div className="activity-meta">
                      {activity.adminId} • {formatDate(activity.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* System Health */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">System Health</h3>
            <Link to="/system" className="admin-btn admin-btn-secondary">
              Details
            </Link>
          </div>
          
          {healthLoading ? (
            <div className="health-list">
              {[1, 2, 3].map((i) => (
                <div key={i} className="health-item">
                  <Skeleton width="100%" height="20px" />
                </div>
              ))}
            </div>
          ) : (
            <div className="health-list">
              <div className="health-item">
                <div className="health-indicator" style={{ background: getHealthColor(systemHealth?.api) }}></div>
                <div className="health-label">API Services</div>
                <div className="health-status">{systemHealth?.api}</div>
              </div>
              <div className="health-item">
                <div className="health-indicator" style={{ background: getHealthColor(systemHealth?.database) }}></div>
                <div className="health-label">Database</div>
                <div className="health-status">{systemHealth?.database}</div>
              </div>
              <div className="health-item">
                <div className="health-indicator" style={{ background: getHealthColor(systemHealth?.payments) }}></div>
                <div className="health-label">Payment Processing</div>
                <div className="health-status">{systemHealth?.payments}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="admin-card admin-section-gap">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Quick Actions</h3>
        </div>
        <div className="quick-actions">
          <Link to="/users" className="quick-action-btn">
            <span className="quick-action-icon">👤</span>
            <span>Manage Users</span>
          </Link>
          <Link to="/promo-codes" className="quick-action-btn">
            <span className="quick-action-icon">🎟️</span>
            <span>Generate Promo Code</span>
          </Link>
          <Link to="/payments" className="quick-action-btn">
            <span className="quick-action-icon">💳</span>
            <span>View Transactions</span>
          </Link>
          <Link to="/analytics" className="quick-action-btn">
            <span className="quick-action-icon">📊</span>
            <span>View Analytics</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getActivityIcon = (action) => {
  const icons = {
    'USER_LOGIN': '🔐',
    'USER_LOGOUT': '🚪',
    'USER_CREATED': '👤',
    'USER_UPDATED': '✏️',
    'USER_DISABLED': '🚫',
    'SUBSCRIPTION_CREATED': '💳',
    'SUBSCRIPTION_CANCELLED': '❌',
    'PAYMENT_RECEIVED': '💰',
    'PROMO_CODE_GENERATED': '🎟️',
    'SETTINGS_UPDATED': '⚙️'
  };
  return icons[action] || '📋';
};

const formatAction = (action) => {
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
};

export default Dashboard;
